import {
  DepressionContentPage,
  FAQItem,
} from "@/components/DepressionContentPage";
import { Link } from "@/lib/router-compat";
import { RelatedLink } from "@/components/RelatedPages";
import { SaintComparisonStatBand } from "@/components/SaintComparisonInline";

const faqs: FAQItem[] = [
  {
    question: "Is 27 too late to start therapy or psychiatric treatment?",
    answer:
      "No. Most adults who eventually get treated for depression waited years — often a decade or more — between their first symptoms and their first appointment. Starting at 27 is not late. It's the beginning. The fact that you're in treatment now is what matters, and today's options are better and faster than they were even ten years ago.",
  },
  {
    question: "What if my first medication doesn't work?",
    answer:
      "Then you try something else, and if that doesn't work either, there are treatments that don't rely on medication at all. The definition of treatment-resistant depression is failing to get adequate relief from antidepressant medication — not failing ten of them. You may qualify for other options, including TMS and SAINT, sooner than you think.",
  },
  {
    question: "How long should treatment take to show results?",
    answer:
      "Effective treatment should produce noticeable change in weeks to months, not years. If a given treatment hasn't produced meaningful improvement after an adequate trial, that's information — not failure. Ask your prescriber how long a treatment should take to show effect and what the plan is if it doesn't. 'Let's see how it goes' is not a plan.",
  },
  {
    question: "Can I still function and qualify for advanced treatment?",
    answer:
      "Yes. Functioning doesn't mean you're fine, and being not-fine doesn't require you to fall apart before you deserve help. You qualify based on your history and your symptoms, not on whether you've stopped functioning. If you've tried medication and you're still meeting criteria for depression, that may be treatment-resistant depression regardless of how it looks from the outside.",
  },
  {
    question: "Do I have to stop therapy or medication to do SAINT?",
    answer:
      "No. Most patients continue their existing therapy and medication regimen through SAINT treatment. SAINT is not a replacement for what you've built — it is a different kind of intervention aimed at the brain circuitry your medications may not have reached. Never change medication without guidance from your prescribing provider.",
  },
  {
    question:
      "How is SAINT different from standard TMS for someone just starting out?",
    answer:
      "Standard TMS requires one session a day, five days a week, for six to nine weeks — 30 to 36 clinic visits. SAINT is delivered over five consecutive days, with ten 10-minute sessions per day. That's one arranged week instead of two months of daily appointments. SAINT also uses an fMRI of your own brain to personalize the treatment target, while standard TMS uses external measurements of the head.",
  },
];

const related: RelatedLink[] = [
  {
    title: "You've Tried Everything. Why Is the Depression Still Here?",
    href: "/youve-tried-everything-why-is-the-depression-still-here",
    description:
      "What treatment-resistant depression means and why medications don't work for everyone.",
  },
  {
    title:
      "Understanding Your PHQ-9 and GAD-7 Scores (And Why 'Moderate' Can Still Mean Severe)",
    href: "/understanding-your-phq-9-and-gad-7-scores",
    description:
      "What the numbers on your intake questionnaires actually mean.",
  },
  {
    title:
      "High-Functioning Depression: When Holding It Together Is the Problem",
    href: "/high-functioning-depression-when-holding-it-together-is-the-problem",
    description:
      "Functioning isn't the same as being okay — and why severe depression hides behind a polished exterior.",
  },
  {
    title: "What Is SAINT TMS Therapy?",
    href: "/learn/what-is-saint-tms",
    description: "How the five-day, fMRI-guided protocol works.",
  },
];

export default function YouJustStartedTherapy() {
  return (
    <DepressionContentPage
      seo={{
        title:
          "You Just Started Therapy at 27. That's Not Late. | Neuro Wellness",
        description:
          "New to therapy or psychiatry in your late twenties? Why starting now matters, what the first weeks feel like, and what comes next if medication isn't enough.",
        canonical: "/you-just-started-therapy-at-27-thats-not-late",
        keywords:
          "starting therapy late, first psychiatrist appointment, treatment-resistant depression, SAINT TMS, depression treatment in your twenties, new to mental health treatment, PHQ-9 GAD-7, when antidepressants don't work",
      }}
      badge="Clinical Insights"
      heroTitle="You Just Started Therapy at 27. That's Not Late. That's the Beginning."
      heroSubtitle="A month ago you saw a therapist for the first time. A few weeks ago you saw a psychiatrist. You're in your late twenties, and you're only now starting to deal with something you've probably had for years. If that's where you are, this is for you."
      lastUpdated="2026-11-03"
      sections={[
        {
          heading: "Why people wait",
          body: (
            <div className="space-y-5">
              <p>
                A month ago you saw a therapist for the first time. A few weeks
                ago you saw a psychiatrist. You're in your late twenties, you
                have a job and a partner and kids, and you're only now starting
                to deal with something you've probably had for years.
              </p>
              <p>
                There's a specific feeling that comes with that. Some relief,
                because you finally did it. Some embarrassment, because it took
                this long. And a lot of uncertainty, because now you're in a
                system you don't understand, being asked questions you don't
                know how to answer, with a treatment plan that's already
                changing.
              </p>

              {/* Visual 1: Starting therapy at 27 is not late */}
              <div className="my-6 overflow-hidden rounded-2xl border border-border shadow-soft bg-card">
                <img
                  src="https://vibe.filesafe.space/1787327131284421342/attachments/4fd7c9b5-25ab-42b1-95b7-751fd5f364b5.png"
                  alt="Starting therapy at 27 is not late — support can begin whenever you're ready"
                  loading="lazy"
                  width={1400}
                  height={1050}
                  className="w-full h-auto object-cover mx-auto bg-card"
                />
              </div>

              <p>
                Most adults who eventually get treated for depression waited
                years, often a decade or more, between the first symptoms and
                the first appointment. The reasons are ordinary and human:
              </p>
              <ul className="list-disc pl-5 space-y-2">
                <li>You assumed everyone felt this way</li>
                <li>You were functioning, so it didn't seem serious enough</li>
                <li>
                  Depression or anxiety was normal in your family, so it didn't
                  register as a problem
                </li>
                <li>
                  You didn't have time, or money, or a clear idea of where to
                  start
                </li>
                <li>
                  Some part of you believed that asking for help meant something
                  was wrong with you
                </li>
              </ul>

              {/* Visual 2: Why people start therapy in their 20s */}
              <div className="my-6 overflow-hidden rounded-2xl border border-border shadow-soft bg-card">
                <img
                  src="https://vibe.filesafe.space/1787327131284421342/attachments/b0862401-1af4-4a34-8b40-c98f75e14a45.png"
                  alt="Why people start therapy in their 20s — big life changes, anxiety & overthinking, burnout or pressure, relationships & identity"
                  loading="lazy"
                  width={1400}
                  height={1050}
                  className="w-full h-auto object-cover mx-auto bg-card"
                />
              </div>

              <p>
                None of those are failures. They're the reasons the system is
                hard to enter. The fact that you're in it now is what matters.
              </p>
            </div>
          ),
        },
        {
          heading: "What the first weeks actually feel like",
          body: (
            <div className="space-y-5">
              <p>
                Nobody tells you this: the beginning of treatment is often
                disorienting. You're meeting new people, filling out
                questionnaires, explaining your history over and over, and being
                handed a diagnosis that may feel bigger than you expected.
                "Major depressive disorder, recurrent, severe" is a jarring
                thing to see written down when you've been telling yourself
                you're just tired.
              </p>

              {/* Visual 3: What a first therapy session may include */}
              <div className="my-6 overflow-hidden rounded-2xl border border-border shadow-soft bg-card">
                <img
                  src="https://vibe.filesafe.space/1787327131284421342/attachments/8043c9dd-62d3-4f7f-b223-abfc03ec138a.png"
                  alt="What a first therapy session may include — a gentle start, not a test: your story, patterns, goals, next steps"
                  loading="lazy"
                  width={1400}
                  height={1050}
                  className="w-full h-auto object-cover mx-auto bg-card"
                />
              </div>

              <p>
                You may also be adjusting to medication changes, or realizing
                that medications you've already tried didn't do what they were
                supposed to. It's common to arrive at treatment having already
                been through an antidepressant or two, a stimulant, a sleep aid,
                and to feel like you're starting over even though you've been at
                this a while.
              </p>
              <p>
                That's not starting over. That's the history your new treatment
                team needs.
              </p>
            </div>
          ),
        },
        {
          heading: "The questions you're afraid to ask",
          body: (
            <div className="space-y-5">
              <p>
                <strong>Is this going to take years?</strong> Not necessarily.
                Therapy is often ongoing, but that doesn't mean feeling like
                this is ongoing. Effective treatment should produce noticeable
                change in weeks to months, not years. If it doesn't, that's
                information, not failure.
              </p>

              {/* Visual 4: Progress is rarely a straight line */}
              <div className="my-6 overflow-hidden rounded-2xl border border-border shadow-soft bg-card">
                <img
                  src="https://vibe.filesafe.space/1787327131284421342/attachments/55a10ade-2f34-47ba-9828-20535f5b0ca1.png"
                  alt="Progress is rarely a straight line — real growth can look uneven and still move forward: awareness, small shifts, hard days, steadier days"
                  loading="lazy"
                  width={1400}
                  height={1050}
                  className="w-full h-auto object-cover mx-auto bg-card"
                />
              </div>

              <p>
                <strong>What if the medication doesn't work?</strong> Then you
                try something else. And if that doesn't work either, there are
                treatments that don't rely on medication at all. You're not at
                the end of the road; you're at the start of a road with more
                branches than most people realize.
              </p>
              <p>
                <strong>Should I have done this sooner?</strong> Probably. So
                should almost everyone. You did it now. That counts.
              </p>
              <p>
                <strong>Is it okay that I'm still functioning?</strong> Yes.
                Functioning doesn't mean you're fine, and being not-fine doesn't
                require you to fall apart before you deserve help.
              </p>
            </div>
          ),
        },
        {
          heading: "Being your own advocate early",
          body: (
            <div className="space-y-4">
              <p>
                Here's what people who've been through the system wish they'd
                known at the start:
              </p>
              <ul className="list-disc pl-5 space-y-2">
                <li>
                  <strong>Keep a record.</strong> Every medication, dose, start
                  and stop date, and what it did. You will be asked for this
                  repeatedly, and your memory will get fuzzy.
                </li>
                <li>
                  <strong>Track your symptoms.</strong> The PHQ-9 and GAD-7
                  questionnaires you fill out are your progress markers. Ask
                  what your scores are and what they mean.
                </li>
                <li>
                  <strong>Set a timeline.</strong> Ask your prescriber how long
                  a given treatment should take to show effect, and what the
                  plan is if it doesn't. "Let's see how it goes" is not a plan.
                </li>
                <li>
                  <strong>Ask about the full range of options.</strong> Therapy
                  and medication are the first steps, not the only steps. The
                  definition of treatment-resistant depression is failing to get
                  adequate relief from antidepressant medication, not failing
                  ten of them. You may qualify for other options sooner than you
                  think.
                </li>
              </ul>
            </div>
          ),
        },
        {
          heading: "Why starting now is different from starting a decade ago",
          body: (
            <div className="space-y-5">
              <p>
                One real advantage of entering treatment today rather than ten
                years ago is that the options are better. Antidepressants are
                still the first line, and for many people they work. But for
                those who don't respond, the chance that yet another medication
                produces full remission is only around 14 percent, and each
                trial takes six to twelve weeks to evaluate. A decade ago, the
                alternatives after that were slow, demanding, or both.
              </p>

              {/* Visual 5: When it may help to ask for more support */}
              <div className="my-6 overflow-hidden rounded-2xl border border-border shadow-soft bg-card">
                <img
                  src="https://vibe.filesafe.space/1787327131284421342/attachments/0a6a7cf9-cd12-4754-9d63-8255ecf3b3a4.png"
                  alt="When it may help to ask for more support — extra support is not failure: care can be layered from therapy to medication support to TMS consultation"
                  loading="lazy"
                  width={1400}
                  height={1050}
                  className="w-full h-auto object-cover mx-auto bg-card"
                />
              </div>

              <p>
                That's changed. Depression isn't only a chemistry problem; it's
                also a circuitry problem. Brain imaging consistently shows that
                the region behind the forehead called the dorsolateral
                prefrontal cortex, which regulates mood, motivation, and
                attention, is underactive in depression. Medication changes the
                chemical environment around that circuit. Transcranial magnetic
                stimulation (TMS) stimulates the circuit directly, using focused
                magnetic pulses through a coil on the scalp, with no anesthesia
                and nothing entering your bloodstream.
              </p>
            </div>
          ),
        },
        {
          heading: "What SAINT means for someone just getting started",
          body: (
            <div className="space-y-4">
              <p>
                SAINT (Stanford Accelerated Intelligent Neuromodulation Therapy)
                is an accelerated, FDA-cleared form of TMS developed at
                Stanford's Brain Stimulation Lab and cleared in 2022. It's
                indicated for adults with major depressive disorder who haven't
                gotten adequate relief from antidepressants in their current
                episode.
              </p>
              <p>
                In the clinical trials behind its clearance, 79 percent of
                participants reached remission, with an average time to
                remission of 2.6 days. Across more than 400 trial patients, 85
                percent experienced symptom relief, with no serious adverse
                events. The most common side effects were mild headache,
                fatigue, and temporary scalp discomfort.
              </p>

              <SaintComparisonStatBand />

              <p>
                Here's how it works at a{" "}
                <Link
                  to="/find-a-verified-saint-provider"
                  className="font-semibold text-primary hover:underline"
                >
                  Verified SAINT Provider
                </Link>{" "}
                like Neuro Wellness TMS Centers of America:
              </p>
              <ol className="list-decimal pl-5 space-y-3">
                <li>
                  <strong>Consultation.</strong> You meet with our clinical team
                  to review your history, including the medications you've
                  already tried, and confirm whether you're a candidate. If
                  you're new to treatment and it's too early, we'll tell you
                  that and explain what would need to happen first. The initial
                  consultation is a Zoom call; as steps evolve, you'll also have
                  the option to meet with our psychiatrist by Zoom web
                  conference or in person. The mandatory in-person steps include
                  fMRI mapping and the actual treatment.
                </li>
                <li>
                  <strong>fMRI brain scan.</strong> A roughly 45-minute
                  functional MRI maps how your individual brain networks are
                  wired.
                </li>
                <li>
                  <strong>Personalized targeting.</strong> A targeting algorithm
                  reads your scan and identifies the exact location for
                  stimulation. Neuronavigation guides the coil to that spot for
                  every session.
                </li>
                <li>
                  <strong>Five-day stimulation series.</strong> Ten 10-minute
                  sessions per day, with 50-minute breaks in between, for five
                  consecutive days. Five clinic visits, compared with 30 to 36
                  for conventional TMS. For a working parent, that's one
                  arranged week, not two months of daily appointments.
                </li>
              </ol>
              <p>
                You don't have to stop your current medications or your therapy.
                Most patients continue their existing regimen through treatment.
                SAINT isn't appropriate for people with non-removable metal in
                or near the head or an implanted device, and you'll be screened
                for these at your consultation.
              </p>
              <p>
                You may not need it. Therapy and medication work for many
                people. But if you've already tried medication and you're still
                meeting criteria for severe depression, you don't have to wait
                until you've exhausted every pill before considering it.
              </p>
            </div>
          ),
        },
        {
          heading: "What to do next",
          body: (
            <div className="space-y-4">
              <p>
                Starting treatment is the hardest step. Knowing what to do when
                the first steps don't fully work is the second hardest. You
                don't have to figure it out alone.
              </p>
              <p>
                At Neuro Wellness TMS Centers of America, we see many people who
                are newly in treatment and already wondering what comes next.
                The first appointment is an evaluation, not a commitment. We'll
                lay out your whole history, give you an honest read on where you
                are, and tell you whether SAINT is an option for you now. We
                offer private outpatient care with a dedicated care team and
                weekend availability. Insurance coverage varies by plan; our
                team can help you understand your options, including prior
                authorization and self-pay.
              </p>
              <p className="font-semibold text-primary">
                Schedule a consultation:{" "}
                <a
                  href="tel:9548279793"
                  className="text-primary hover:underline"
                >
                  954-827-9793
                </a>
              </p>
              <p className="text-xs text-muted-foreground italic pt-2">
                If you are in crisis or thinking about harming yourself, please
                call or text 988 (Suicide and Crisis Lifeline) or go to your
                nearest emergency room. This article is for educational purposes
                and is not a substitute for individual medical advice.
              </p>
            </div>
          ),
        },
      ]}
      faq={faqs}
      relatedLinks={related}
      finalCtaText="Schedule a Consultation"
    />
  );
}
